// @flow
import classnames from 'classnames';
import React, { Component, type Node } from 'react';
import Sticky from 'react-stickynode';

import { Scrollable } from 'Components/frameworks/scrollable/Scrollable.jsx';
import { COLORS } from 'Components/utils/color/colors.js';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { withOpenAndScrollToHash } from 'Components/utils/tabs/tabs.hoc.jsx';
import { ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, ARROW_UP, END, HOME } from 'Utils/keyCodes';

import { NavigationTabsHeader } from './header/NavigationTabsHeader.jsx';
import { getClassNames } from './NavigationTabs.utils';

type Props = {
  className?: string,
  initialSelectedItem?: number,
  id: string,
  items: Array<{
    color?: Colors,
    content: Array<Node>,
    id: string,
    title: string,
    trackingData?: ?TrackingData,
  }>,
  scrollBackwardLabel?: string,
  scrollForwardLabel?: string,
};

type State = {
  index: number,
  initialOffsetLeft: number,
};

class _NavigationTabs extends Component<Props, State> {
  static defaultProps = {
    initialSelectedItem: 0,
    items: [],
  };

  refsArray: Array<HTMLDivElement | null>;

  constructor(props) {
    super(props);
    this.refsArray = [];
    this.state = {
      index: props.initialSelectedItem,
      initialOffsetLeft: 0,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.initialSelectedItem !== this.props.initialSelectedItem) {
      const initialSelectedItem = this.props.initialSelectedItem || 0;

      this.setState({
        index: initialSelectedItem,
        initialOffsetLeft: this.getInitialOffsetLeft(initialSelectedItem),
      });
    }
  }

  componentDidMount() {
    this.setState({
      initialOffsetLeft: this.getInitialOffsetLeft(this.props.initialSelectedItem),
    });
    if (this.refsArray.length) {
      // $FlowFixMe
      this.refsArray.forEach(ref => ref.addEventListener('keydown', this.onKeyDown, false));
    }
  }

  componentWillUnmount() {
    if (this.refsArray.length) {
      // $FlowFixMe
      this.refsArray.forEach(ref => ref.removeEventListener('keydown', this.onKeyDown, false));
    }
  }

  getInitialOffsetLeft(initialSelectedItem = 0) {
    const ref = this.refsArray[initialSelectedItem];
    return ref ? ref.offsetLeft : 0;
  }

  onKeyDown = (event: KeyboardEvent) => {
    const { items } = this.props;

    if (items.length > 1) {
      this.setState(
        ({ index }) => {
          switch (event.which) {
            case ARROW_RIGHT:
            case ARROW_DOWN:
              event.preventDefault();
              return { index: index === items.length - 1 ? 0 : index + 1 };
            case ARROW_LEFT:
            case ARROW_UP:
              event.preventDefault();
              return { index: index === 0 ? items.length - 1 : index - 1 };
            case HOME:
              event.preventDefault();
              return { index: 0 };
            case END:
              event.preventDefault();
              return { index: items.length - 1 };
            default:
          }
        },
        () =>
          this.refsArray[this.state.index] && this.refsArray[this.state.index].firstChild.focus(),
      );
    }
  };

  render() {
    const {
      className,
      id: mainId = 'NavigationTabs',
      items,
      scrollBackwardLabel,
      scrollForwardLabel,
    } = this.props;

    if (!items.length) {
      return null;
    }

    const { index, initialOffsetLeft } = this.state;
    const activeItem = items[index] || {};

    const activeColor = activeItem.color || COLORS.BLUE;

    const content = items.map(({ color, id, title, trackingData }, idx) => (
      <div
        className={classnames('border-gray-light', getClassNames(items.length), {
          'rtl:border-l-1': idx !== 0,
        })}
        key={id || title}
        ref={ref => {
          this.refsArray[idx] = ref;
        }}
      >
        <NavigationTabsHeader
          className="w-full"
          color={color}
          controlsId={`${id}-tab`}
          id={id}
          isActive={idx === index}
          onClick={() => {
            this.setState({ index: idx }, () => {
              scrollToElement(`#${mainId}`, 100);
              // $FlowFixMe
              this.refsArray[idx].firstChild.focus();
            });
          }}
          trackingData={trackingData}
        >
          {title}
        </NavigationTabsHeader>
      </div>
    ));

    return (
      <div className={className} id={mainId}>
        <Sticky bottomBoundary="#NavigationTabsBeacon" innerZ={5}>
          <div className="lg:px-5">
            <div
              className={`bg-white border-b-2 border-${activeColor} lg:max-w-site lg:m-auto shadow-lg`}
            >
              <Scrollable
                backwardLabel={scrollBackwardLabel}
                className="border-t-1 lg:border-l-1 lg:border-r-1 lg:-m-px border-gray-light"
                forwardLabel={scrollForwardLabel}
                hasShadow
                initialScrollLeft={initialOffsetLeft}
                navPosition="center"
              >
                <div className="bg-white flex" role="tablist">
                  {content}
                </div>
              </Scrollable>
            </div>
          </div>
        </Sticky>
        <div
          aria-labelledby={activeItem.id}
          className="outline-none"
          id={`${activeItem.id}-tab`}
          role="tabpanel"
        >
          {activeItem.content}
        </div>
        <div id="NavigationTabsBeacon" />
      </div>
    );
  }
}

export const NavigationTabs = withOpenAndScrollToHash(_NavigationTabs);
